import { twMerge } from 'tailwind-merge';

import { InternalLink } from '../../../../components/Clickables/InternalLink/InternalLink';
import { Heading } from '../../../../components/Heading/Heading';
import { Text } from '../../../../components/Text/Text';
import { getEdgesFromConnection } from '../../../../helpers/graphql';
import { getRegionUrl, getSpeciesUrl } from '../../../../helpers/urls';
import {
  GetPlace_entry_country,
  GetPlace_entry_region,
  GetPlaceTopAnglers_entry_topAnglers_anglers,
  GetPlaceTopSpecies_entry_topSpeciesSummary,
  GetPlaceTopSpecies_entry_topSpeciesSummary_edges,
  GetPlaceTopSpecies_entry_topSpeciesSummary_edges_node,
} from '../../../../interfaces/graphql';
import ReactAnd from 'react-and';
import { PLACE_SUB_LINK_IDS } from '../../helpers/getPlaceSubNavLinks';
import { OtherAnglers } from '../../../../components/OtherAnglers/OtherAnglers';
import { convertCount } from '../../../../helpers/conversion';

interface IProps {
  country: GetPlace_entry_country | null;
  placeId: string;
  placeName: string;
  region: GetPlace_entry_region | null;
  topSpeciesSummary?: GetPlaceTopSpecies_entry_topSpeciesSummary;
  className?: string;
  topAnglers: GetPlaceTopAnglers_entry_topAnglers_anglers | null;
  topAnglersDateString: string;
}

export const GeneralInfoSection = ({
  country,
  placeId,
  placeName,
  region,
  topSpeciesSummary,
  className,
  topAnglers,
  topAnglersDateString,
}: IProps) => {
  const countryName = country?.countryName;
  const countryId = country?.id;
  const regionName = region?.name;
  const regionId = region?.id;
  const hasRegionInfo = regionName && regionId && countryName && countryId;
  const regionUrl = hasRegionInfo && getRegionUrl(countryId, regionId, countryName, regionName);

  const speciesEdges = getEdgesFromConnection<
    GetPlaceTopSpecies_entry_topSpeciesSummary_edges_node,
    GetPlaceTopSpecies_entry_topSpeciesSummary_edges
  >(topSpeciesSummary);

  const top3Species = speciesEdges.slice(0, 3);

  return (
    <div
      id={PLACE_SUB_LINK_IDS.GENERAL_INFO}
      className={twMerge(
        'w-full scroll-mt-[--navigation-scrolltop-offset-mobile] sm:scroll-mt-[--navigation-scrolltop-offset]',
        className,
      )}
    >
      <div className="flex size-full flex-col justify-center gap-8 bg-lake-fog px-8 py-10 sm:rounded-xl sm:px-10 md:px-12">
        <div className="flex flex-col gap-2">
          <Heading variant="sm" as="h3">
            {`About ${placeName} fishing`}
          </Heading>
          <Text variant="md" className="m-0">
            {countryId && countryName && (
              <span>
                {`Check out the best fishing spots in and around ${placeName}, `}
                {regionUrl ? (
                  <InternalLink className="text-primary" to={regionUrl}>
                    {regionName}
                  </InternalLink>
                ) : (
                  regionName
                )}
                {'. '}
              </span>
            )}
            {top3Species.length > 0 && (
              <span>
                {`Anglers using Fishbrain have logged: `}
                <ReactAnd>
                  {top3Species.map(({ node, catchesCount }) => {
                    return (
                      <span key={`species-${node._id}`}>
                        {`${convertCount(catchesCount)} ${catchesCount === 1 ? ' catch' : ' catches'} for `}
                        <InternalLink
                          to={getSpeciesUrl(node._id, node.displayName)}
                          className="text-primary"
                        >
                          {node.displayName}
                        </InternalLink>
                      </span>
                    );
                  })}
                </ReactAnd>
                {`.`}
              </span>
            )}
          </Text>
        </div>
        {topAnglers && topAnglers.totalCount > 0 && (
          <OtherAnglers
            appDownloadSourceId={placeId}
            topAnglers={topAnglers}
            topAnglersDateString={topAnglersDateString}
          />
        )}
      </div>
    </div>
  );
};
